import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  format,
  intervalToDuration,
  isAfter,
  isBefore,
  isValid,
  parse,
  parseISO,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subYears,
} from 'date-fns'
import { ru } from 'date-fns/locale'

import { NString } from 'types'

import { isDef } from './utils'

/**
 * Преобразовывает строку даты.
 * @param {string} date Дата в формате "dd.MM.yyyy".
 * @returns {string} Возвращает даты в формате "yyyy-MM-dd".
 */
export const formatDateToISO = (date: string) => {
  if (!date) return date

  const parsedDate = parse(date, 'dd.MM.yyyy', new Date())
  if (!isValid(parsedDate)) return date

  return format(parsedDate, 'yyyy-MM-dd')
}

/**
 * Преобразовывает строку даты.
 * @param {string} date Дата в формате "yyyy-MM-dd".
 * @returns {string} Возвращает даты в формате "dd.MM.yyyy".
 */
export const formatISOToInputDate = (date: string) => {
  if (!date) return date

  const parsedDate = parse(date, 'yyyy-MM-dd', new Date())

  if (!isValid(parsedDate)) return date

  return format(parsedDate, 'dd.MM.yyyy')
}

/**
 * Преобразовывает дату из формата ISO в нужный формат
 * @param {string} date Дата в формате ISO.
 * @param {format} formatString Формат даты.
 * @returns {string} Возвращает дату в нужном формате.
 */
export const formatDate = (date: string | Date, formatString: string): string => {
  if (!date) return date

  const parsedDate = typeof date === 'string' ? parseISO(date) : date

  return isValid(parsedDate) ? format(parsedDate, formatString, { locale: ru }) : date.toString()
}

/**
 * Форматирует количество секунд в строку в формате "X д Y ч Z мин"
 * @param seconds - количество секунд (может быть отрицательным)
 */
export const formatDurationFromSeconds = (seconds: number) => {
  if (!isDef(seconds)) {
    return '...'
  }

  const isNegative = seconds < 0
  const absSeconds = Math.abs(seconds)

  if (absSeconds < 60 && !isNegative) {
    return '< 1 мин'
  }

  const duration = intervalToDuration({
    start: 0,
    end: absSeconds * 1000,
  })

  const { years, months, days, hours, minutes } = duration
  const parts = []

  if (years && years > 0) parts.push(`${duration.years} г`)
  if (months && months > 0) parts.push(`${duration.months} мес`)
  if (days && days > 0) parts.push(`${duration.days} д`)
  if (hours && hours > 0) parts.push(`${duration.hours} ч`)
  if (minutes && minutes > 0) parts.push(`${duration.minutes} мин`)

  let result = parts.join(' ')

  if (isNegative) {
    result = `через ${result}`
  }

  return result
}

export const isISOValid = (date: string) => {
  if (!date) {
    return false
  }

  const parsedDate = parseISO(date)
  return isValid(parsedDate)
}

export const isFutureDate = (date: string) => {
  const now = new Date()

  return isBefore(now, date)
}

export const calculateDurationInSecond = (startISO: NString, endISO: NString) => {
  if (!startISO || !endISO) return 0

  const start = parseISO(startISO)
  const end = parseISO(endISO)

  if (!start || !end || start > end) return 0

  return differenceInSeconds(end, start)
}

export const secondsInTimeFormat = (duration: number) => {
  const pad = (v: number) => v.toString().padStart(2, '0')
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration - hours * 3600) / 60)
  const seconds = duration - hours * 3600 - minutes * 60

  return `${pad(minutes)}:${pad(seconds)}`
}

export const secondsInHHFormat = (duration: number) => {
  const hours = Math.floor(duration / 3600)
  return `${hours.toString().padStart(2, '0')}`
}

export const formatDuration = (startISO: NString, endISO: NString) => {
  const duration = calculateDurationInSecond(startISO, endISO)
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration - hours * 3600) / 60)
  const seconds = duration - hours * 3600 - minutes * 60

  let result = ''
  if (hours) result = `${hours} ч`
  if (minutes) result = [result, `${minutes} мин`].filter(Boolean).join(' ')
  if (seconds) result = [result, `${seconds} сек`].filter(Boolean).join(' ')
  return result
}

export const calculateDuration = (startISO: NString, endISO: NString) => {
  if (!startISO || !endISO) return ''

  const start = parseISO(startISO)
  const end = parseISO(endISO)

  if (!start || !end || start > end) return ''

  let remainingDate = end

  const years = differenceInYears(remainingDate, start)
  remainingDate = subYears(remainingDate, years)

  const months = differenceInMonths(remainingDate, start)
  remainingDate = subMonths(remainingDate, months)

  const days = differenceInDays(remainingDate, start)
  remainingDate = subDays(remainingDate, days)

  const hours = differenceInHours(remainingDate, start)
  remainingDate = subHours(remainingDate, hours)

  const minutes = differenceInMinutes(remainingDate, start)
  remainingDate = subMinutes(remainingDate, minutes)

  const seconds = differenceInSeconds(remainingDate, start)

  const parts = []
  if (years > 0) parts.push(`${years}${years > 4 ? 'л' : 'г'}`)
  if (months > 0) parts.push(`${months}мec`)
  if (days > 0) parts.push(`${days}д`)
  if (hours > 0) parts.push(`${hours}ч`)
  if (minutes > 0) parts.push(`${minutes}м`)
  if (seconds > 0) parts.push(`${seconds}с`)

  return parts.join(' ')
}

export const calculateAge = (dateString: string): string => {
  if (!dateString) {
    return ''
  }

  const startDate = parseISO(dateString)
  const now = new Date()

  const duration = intervalToDuration({ start: startDate, end: now })

  if (!duration) {
    return ''
  }

  const { years, months, days } = duration

  const parts = []

  if (years) parts.push(`${Math.abs(years)} г`)
  if (months) parts.push(`${Math.abs(months)} мес`)
  if (days) parts.push(`${Math.abs(days)} дн`)

  const ageString = parts.join(' ')

  return isAfter(startDate, now) ? `через ${ageString}` : ageString
}

export const isValidDate = (date: NString) => {
  const parsedDate = parseISO(date || '')
  return isValid(parsedDate)
}

export const isBeforeCurrent = (dateISO: string) => {
  const parsedDate = parseISO(dateISO)
  return isValid(parsedDate) && isBefore(parsedDate, new Date())
}

export const getShortWeekday = (date: Date) => {
  const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
  return days[date.getDay()]
}

export const getShortMonth = (date: Date) => {
  const months = [
    'Янв',
    'Фев',
    'Мар',
    'Апр',
    'Май',
    'Июн',
    'Июл',
    'Авг',
    'Сен',
    'Окт',
    'Ноя',
    'Дек',
  ]
  return months[date.getMonth()]
}
